import { ArticleSummary, RelatedDocument } from '../../types';

// COM-641 task 2: turns an `ArticleSummary | null` into the ordered list of
// sections `ArticleSummaryPanel` renders. Pure and TDD'd (see
// summarySections.test.ts) so the AC's field order, the "Not specified"
// empty rule, and the default-expanded set are all testable without
// mounting any React.

export type SummarySectionKind = 'paragraph' | 'list' | 'links';

interface BaseSummarySection {
  /** the `ArticleSummary` field this section renders - also the React key */
  key: string;
  heading: string;
  /** true when the underlying field is absent or an empty array/string -
   * the panel renders the literal "Not specified" (AC wording) instead of
   * `value` when this is true, rather than baking that string in here, so
   * `value` always stays the field's own natural shape (string / string[] /
   * RelatedDocument[]) for a caller that wants the real data. */
  isEmpty: boolean;
  /** Figma draws both states; only the sections that carry the reading
   * value (key points, actions required) open by default - the rest start
   * collapsed so the panel opens on substance, not a wall of labels. */
  defaultExpanded: boolean;
}

export interface ParagraphSummarySection extends BaseSummarySection {
  kind: 'paragraph';
  value: string;
}

export interface ListSummarySection extends BaseSummarySection {
  kind: 'list';
  value: string[];
}

export interface LinksSummarySection extends BaseSummarySection {
  kind: 'links';
  value: RelatedDocument[];
}

export type SummarySection = ParagraphSummarySection | ListSummarySection | LinksSummarySection;

function paragraphSection(
  key: string,
  heading: string,
  value: string | undefined,
  defaultExpanded = false
): ParagraphSummarySection {
  return { key, heading, kind: 'paragraph', value: value ?? '', isEmpty: !value, defaultExpanded };
}

function listSection(
  key: string,
  heading: string,
  value: string[] | undefined,
  defaultExpanded = false
): ListSummarySection {
  return { key, heading, kind: 'list', value: value ?? [], isEmpty: !value || value.length === 0, defaultExpanded };
}

function linksSection(
  key: string,
  heading: string,
  value: RelatedDocument[] | undefined,
  defaultExpanded = false
): LinksSummarySection {
  return { key, heading, kind: 'links', value: value ?? [], isEmpty: !value || value.length === 0, defaultExpanded };
}

/**
 * Builds the AC-ordered section list for a summary, or `null` when there is
 * no summary at all - a `null` summary is its own panel state (see
 * `ArticleSummaryPanel`'s no-summary rendering), never eight sections that
 * all happen to read "Not specified".
 */
export function buildSummarySections(summary: ArticleSummary | null): SummarySection[] | null {
  if (!summary) return null;

  return [
    // COM-641 correction: the AC (Confluence 400293892) asks for ONE field
    // here - "author or the authority that issued the document" - not a
    // separate issuingAuthority; authorInfo already carries this (the dev DB
    // showed the model fills it with the issuing body, e.g. "Financial
    // Conduct Authority (FCA)"), so this section reads authorInfo, not a
    // second field.
    paragraphSection('authorInfo', 'Author / issuing authority', summary.authorInfo),
    listSection('organizations', 'Types of organisations affected', summary.organizations),
    paragraphSection('jurisdiction', 'Jurisdiction', summary.jurisdiction),
    listSection('keyPoints', 'Most important points', summary.keyPoints, true),
    listSection('actionsRequired', 'Actions required', summary.actionsRequired, true),
    listSection('importantDates', 'Important dates', summary.importantDates),
    paragraphSection('financialInfo', 'Financial side', summary.financialInfo),
    linksSection('relatedDocuments', 'Related documents', summary.relatedDocuments),
  ];
}
